// Hook partilhado do tema do planner (claro/escuro), a seguir o tema da barra
// (menu do avatar → localStorage 'plannerToolbarTheme'). Clássico e Claro
// resolvem para 'light'; Escuro para 'dark'. Reage ao vivo via evento próprio
// 'plannerThemeChange' (mesmo separador) e 'storage' (outros separadores).
//
// Uso: const theme = usePlannerTheme();  → pôr data-planner-theme={theme} na
// raiz do ecrã (ou no PaperProps de um Dialog) e consumir os tokens --surf-*
// definidos em plannerSurface.css.
import { useState, useEffect, useMemo } from 'react';
import { createMuiTheme } from '@material-ui/core/styles';

export function readPlannerTheme() {
  try { return localStorage.getItem('plannerToolbarTheme') === 'dark' ? 'dark' : 'light'; }
  catch { return 'light'; }
}

// Identidade ecooGPS (gradiente de marca) — partilhada pelos ecrãs que adoptam o
// novo UI em Claro/Escuro. ON_BRAND é a cor de texto por cima do gradiente.
export const BRAND_GRADIENT = 'linear-gradient(100deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)';
export const BRAND_GRADIENT_DARK = 'linear-gradient(100deg, #6EC1E4 0%, #20C6F4 45%, #3DDC97 100%)';
export const ON_BRAND = '#03121c';
export function brandGradient(theme) { return theme === 'dark' ? BRAND_GRADIENT_DARK : BRAND_GRADIENT; }

// Tema MUI para DIÁLOGOS: envolver um <Dialog> em <ThemeProvider theme={…}>
// faz o Paper, inputs (TextField/Select), labels e menus seguirem o modo
// claro/escuro automaticamente — a forma robusta de temar formulários MUI.
// A paleta espelha os tokens --surf-* (navy no escuro, índigo de marca).
export function usePlannerDialogTheme() {
  const theme = usePlannerTheme();
  return useMemo(() => createMuiTheme({
    palette: {
      type: theme,
      primary: { main: theme === 'dark' ? '#818cf8' : '#4f46e5' },
      background: {
        paper: theme === 'dark' ? '#122133' : '#ffffff',
        default: theme === 'dark' ? '#0b1522' : '#f8fafc',
      },
      text: {
        primary: theme === 'dark' ? '#eaf3fb' : '#0f172a',
        secondary: theme === 'dark' ? '#9fb3c8' : '#64748b',
      },
      divider: theme === 'dark' ? 'rgba(110,193,228,0.16)' : 'rgba(15,23,42,0.08)',
    },
  }), [theme]);
}

export function usePlannerTheme() {
  const [theme, setTheme] = useState(readPlannerTheme);
  useEffect(() => {
    const sync = () => setTheme(readPlannerTheme());
    window.addEventListener('plannerThemeChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('plannerThemeChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return theme;
}

// Variante de TRÊS estados: 'classic' | 'light' | 'dark'. Necessária onde o novo
// UID só deve aparecer em Claro/Escuro e o Clássico fica com o visual antigo
// (o usePlannerTheme junta classic+light em 'light'). Reage aos mesmos eventos.
export function readPlannerToolbarTheme() {
  try {
    const t = localStorage.getItem('plannerToolbarTheme');
    return t === 'dark' ? 'dark' : t === 'light' ? 'light' : 'classic';
  } catch { return 'classic'; }
}

export function usePlannerToolbarTheme() {
  const [theme, setTheme] = useState(readPlannerToolbarTheme);
  useEffect(() => {
    const sync = () => setTheme(readPlannerToolbarTheme());
    window.addEventListener('plannerThemeChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('plannerThemeChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return theme;
}
